import { useEffect, useMemo, useState } from 'react';
import 'moment/locale/es';
import { useTranslation } from 'react-i18next';
import Loader from '../loader/loader.component';
import Icon from '../../components/forms/icon.component';

declare const KTComponents: any;

interface ColumnType {
  name: any;
  sortable?: boolean;
  center?: boolean;
  exportable?: boolean;
  selector?: (row: any) => any;
  formatToExport?: (row: any) => any;
  cell?: (row: any) => React.ReactNode;
  width?: number;
  filterColumn?: boolean;
}

interface CustomDataTableProps {
  columns: Array<ColumnType>;
  data: Array<any>;
  filters?: Array<React.ReactNode>;
  showDates?: boolean;
  showExport?: boolean;
  onExport?: (format: 'csv' | 'excel') => void;
  extraExportActions?: React.ReactNode;
  singleAccounts?: boolean;
  filter?: (row: any, term: string) => any;
  hideAccountsFilter?: boolean;
  isLoading?: boolean;
  order?: { columnIndex: number; direction: 'asc' | 'desc' }; // orden inicial opcional
}

function CustomDataTable(props: CustomDataTableProps) {
  const { t } = useTranslation();

  const [hideColumns] = useState<boolean[]>([]);
  const [columns, setColumns] = useState<Array<ColumnType>>(props.columns);
  const [rows, setRows] = useState<Array<any>>(props.data);
  const [term, setTerm] = useState<string>('');

  const [filtersColumns, setFiltersColumns] = useState<string[]>([]);
  const getInitialSort = () => {
    if (props.order) return props.order;
    return { columnIndex: 0, direction: 'desc' };
  };

  const [sortConfig, setSortConfig] = useState(getInitialSort);

  const [paginationConfig, setPaginationConfig] = useState<{
    totalPages: number;
    currentPage: number;
    rowsPerPage: number;
    isFirst: boolean;
    isLast: boolean;
    rows: React.ReactNode[];
  }>({
    totalPages: 1,
    currentPage: 0,
    rowsPerPage: 10,
    isFirst: true,
    isLast: false,
    rows: []
  });

  useEffect(() => {
    setFiltersColumns(new Array(props.columns.length).fill(''));
  }, [props.columns.length]);

  useEffect(() => {
    setColumns(props.columns);
    setRows(props.data);
  }, [props.columns, props.data]);

  const columnsFiltered = useMemo(() => {
    return columns.filter((_, index) => !hideColumns[index]);
  }, [columns, hideColumns]);

  const sortedRows = useMemo(() => {
    if (!sortConfig) return rows;
    const { columnIndex, direction } = sortConfig;
    const col = columns[columnIndex];
    if (!col) return rows;

    return [...rows].sort((a, b) => {
      const aValue = col.selector ? col.selector(a) : a;
      const bValue = col.selector ? col.selector(b) : b;

      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return direction === 'asc' ? aValue - bValue : bValue - aValue;
      }

      const aStr = String(aValue).toLowerCase();
      const bStr = String(bValue).toLowerCase();

      if (aStr < bStr) return direction === 'asc' ? -1 : 1;
      if (aStr > bStr) return direction === 'asc' ? 1 : -1;
      return 0;
    });
  }, [rows, sortConfig, columns]);

  const filteredRows = useMemo(() => {
    return sortedRows.filter((row) => {
      if (term) {
        if (props.filter) {
          if (!props.filter(row, term)) return false;
        } else {
          const searchKeys = Object.values(row).join(' ').toLowerCase();
          if (!searchKeys.includes(term.toLowerCase())) return false;
        }
      }

      for (let i = 0; i < filtersColumns.length; i++) {
        const filterVal = filtersColumns[i];
        if (filterVal) {
          const col = columns[i];
          if (!col) continue;
          const cellVal = col.selector ? col.selector(row) : '';
          if (!String(cellVal).toLowerCase().includes(filterVal.toLowerCase())) {
            return false;
          }
        }
      }
      return true;
    });
  }, [sortedRows, term, filtersColumns, props.filter, columns]);

  const rowsOfTable = useMemo(() => {
    const start = paginationConfig.currentPage * paginationConfig.rowsPerPage;
    return filteredRows.slice(start, start + paginationConfig.rowsPerPage).map((row, index) => (
      <tr key={index}>
        {columnsFiltered.map((column, indexCol) => (
          <td key={`row-${index}-col-${indexCol}`} className={column.center ? 'text-center' : ''}>
            {column.cell ? column.cell(row) : (column.selector && column.selector(row)) || ''}
          </td>
        ))}
      </tr>
    ));
  }, [filteredRows, columnsFiltered, paginationConfig]);

  useEffect(() => {
    const totalPages = Math.ceil(filteredRows.length / paginationConfig.rowsPerPage);
    const start = 0;
    const rows = filteredRows
      .slice(start, start + paginationConfig.rowsPerPage)
      .map((row, index) => (
        <tr key={index}>
          {columnsFiltered.map((column, indexCol) => (
            <td key={`row-${index}-col-${indexCol}`} className={column.center ? 'text-center' : ''}>
              {column.cell ? column.cell(row) : (column.selector && column.selector(row)) || ''}
            </td>
          ))}
        </tr>
      ));

    setPaginationConfig((prevConfig) => ({
      ...prevConfig,
      totalPages,
      currentPage: 0,
      isFirst: true,
      isLast: totalPages <= 1,
      rows
    }));
  }, [filteredRows, paginationConfig.rowsPerPage, columnsFiltered]);

  // Manejar cambio de página
  const handleNextPrevPage = (action: 'prev' | 'next' | 'first' | 'last') => {
    let currentPage = paginationConfig.currentPage;
    switch (action) {
      case 'prev':
        currentPage--;
        break;
      case 'next':
        currentPage++;
        break;
      case 'first':
        currentPage = 0;
        break;
      case 'last':
        currentPage = paginationConfig.totalPages - 1;
        break;
    }
    if (currentPage < 0) currentPage = 0;
    if (currentPage >= paginationConfig.totalPages) currentPage = paginationConfig.totalPages - 1;

    const isFirst = currentPage === 0;
    const isLast = currentPage === paginationConfig.totalPages - 1;

    const start = currentPage * paginationConfig.rowsPerPage;
    const rows = filteredRows
      .slice(start, start + paginationConfig.rowsPerPage)
      .map((row, index) => (
        <tr key={index}>
          {columnsFiltered.map((column, indexCol) => (
            <td key={`row-${index}-col-${indexCol}`} className={column.center ? 'text-center' : ''}>
              {column.cell ? column.cell(row) : (column.selector && column.selector(row)) || ''}
            </td>
          ))}
        </tr>
      ));

    setPaginationConfig({
      ...paginationConfig,
      currentPage,
      isFirst,
      isLast,
      rows
    });
  };

  const handleSortClick = (index: number) => {
    if (!columns[index].sortable) return;

    setSortConfig((currentSort) => {
      if (currentSort && currentSort.columnIndex === index) {
        const newDir = currentSort.direction === 'asc' ? 'desc' : 'asc';
        return { columnIndex: index, direction: newDir };
      }
      return { columnIndex: index, direction: 'asc' };
    });
  };

  const renderSortIcon = (index: number) => {
    const isActive = sortConfig && sortConfig.columnIndex === index;
    const direction = isActive ? sortConfig!.direction : null;
    const activeColor = '#579743';

    if (!isActive) return null;

    return (
      <div className="d-flex flex-column ms-1">
        {direction === 'asc' && (
          <i
            className="ki-duotone ki-up fw-bolder fs-1"
            style={{ color: activeColor, fontSize: '12px' }}
          />
        )}
        {direction === 'desc' && (
          <i
            className="ki-duotone ki-down fw-bolder fs-1"
            style={{ color: activeColor, fontSize: '12px' }}
          />
        )}
      </div>
    );
  };

  // Manejar cambio filtro por columna
  const handleColumnFilterChange = (index: number, value: string) => {
    setFiltersColumns((prev) => {
      const newFilters = [...prev];
      newFilters[index] = value;
      return newFilters;
    });
  };

  // Headers con click para ordenar e input si filterColumn
  const getHeaders = useMemo(() => {
    return columnsFiltered.map((column, idx) => {
      const realIndex = columns.findIndex((col) => col === column);
      const hasFilter = column.filterColumn;

      return (
        <th
          key={idx}
          scope="col"
          className={`${column.center ? 'text-center' : ''} ${column.sortable ? 'cursor-pointer' : ''}`}
          style={{
            width: column.width ? `${column.width}%` : 'auto',
            minWidth: column.width ? `${column.width}%` : '150px',
            maxWidth: column.width ? `${column.width}%` : '200px',
            whiteSpace: 'nowrap',
            verticalAlign: 'top'
          }}
          onClick={() => handleSortClick(realIndex)}
        >
          <div className="d-flex flex-column align-items-stretch">
            <div className="d-flex align-items-center justify-content-between">
              <span>{column.name}</span>
              {renderSortIcon(realIndex)}
            </div>

            {hasFilter && (
              <input
                type="text"
                value={filtersColumns[realIndex] || ''}
                onClick={(e) => e.stopPropagation()}
                onChange={(e) => handleColumnFilterChange(realIndex, e.target.value)}
                placeholder={'Filtrar'}
                className="form-control form-control-solid"
                style={{
                  width: '100%',
                  maxWidth: '100%',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis'
                }}
              />
            )}
          </div>
        </th>
      );
    });
  }, [columnsFiltered, filtersColumns, sortConfig, columns, t]);

  useEffect(() => {
    KTComponents.init();
  });

  useEffect(() => {
    const handleLanguageChange = () => {
      setColumns([...props.columns]);
      setRows([...props.data]);
      KTComponents.init();
    };
    window.addEventListener('languageChanged', handleLanguageChange);
    return () => {
      window.removeEventListener('languageChanged', handleLanguageChange);
    };
  }, [props.columns, props.data]);

  if (props.isLoading) {
    return <Loader />;
  }

  return (
    <>
      <div className="d-flex align-items-center position-relative my-1 mb-10">
        <Icon icon="ki-magnifier" size="fs-3" type="danger" className="position-absolute ms-4" />
        <input
          className="form-control form-control-solid w-250px ps-12"
          placeholder={t('datatable.input-search')}
          type="text"
          value={term}
          onChange={({ target: { value } }) => setTerm(value)}
        />
      </div>
      <div className="relative">
        {rows.length === 0 && (
          <div className="text-center p-4">
            <img
              alt="imageCh"
              title="empty-table"
              className="mb-3 animated-Alert"
              src="/assets/media/illustrations/undraw/empty-data.svg"
              width="40%"
            />
            <p className="mb-0 fs-3">{t('datatable.empty-records')}</p>
          </div>
        )}
        {props.isLoading && <Loader />}
      </div>
      {rows.length > 0 && (
        <div className="table-responsive">
          <table className="table table-row-bordered table-striped gs-3">
            <thead className="thead-light">
              <tr className="text-start text-gray-700 fw-bold fs-5 gs-0">{getHeaders}</tr>
            </thead>
            <tbody>{paginationConfig.rows}</tbody>
          </table>
        </div>
      )}
      {filteredRows.length === 0 && rows.length > 0 && (
        <div className="text-center p-4">
          <img
            alt="imageCh"
            title="empty-table"
            className="mb-3 animated-Alert"
            src="/assets/media/illustrations/undraw/no-find.svg"
            width="25%"
          />
          <p className="mb-0 fs-3">{t('datatable.zero-records')}</p>
        </div>
      )}
      {rows.length > 0 && (
        <nav className="p-4 d-flex justify-content-between align-items-center">
          <div className="d-flex align-items-center">
            <select
              title="select"
              className="form-select form-select-sm w-auto ms-2 form-select-solid"
              onChange={({ target: { value } }) =>
                setPaginationConfig({ ...paginationConfig, rowsPerPage: +value })
              }
              value={paginationConfig.rowsPerPage}
            >
              <option value="10">10</option>
              <option value="15">15</option>
              <option value="20">20</option>
              <option value="25">25</option>
              <option value="30">30</option>
            </select>
            <small className="text-gray-700 ms-2">{t('datatable.lenght-menu')}</small>
          </div>
          <div className="d-flex align-items-center">
            <small className="text-gray-700 mx-4">
              {paginationConfig.currentPage * paginationConfig.rowsPerPage + 1} -{' '}
              {Math.min(
                (paginationConfig.currentPage + 1) * paginationConfig.rowsPerPage,
                filteredRows.length
              )}{' '}
              {t('general.of')} {filteredRows.length}
            </small>
            <button
              title="btn-pagination"
              type="button"
              className="btn btn-sm btn-icon btn-active-light-primary mx-1"
              disabled={paginationConfig.isFirst}
              onClick={() => handleNextPrevPage('first')}
            >
              <i className="bi bi-chevron-bar-left fs-3"></i>
            </button>
            <button
              title="btn-pagination"
              type="button"
              className="btn btn-sm btn-icon btn-active-light-primary mx-1"
              disabled={paginationConfig.isFirst}
              onClick={() => handleNextPrevPage('prev')}
            >
              <i className="bi bi-chevron-left fs-3"></i>
            </button>
            <button
              title="btn-pagination"
              type="button"
              className="btn btn-sm btn-icon btn-active-light-primary mx-1"
              disabled={paginationConfig.isLast}
              onClick={() => handleNextPrevPage('next')}
            >
              <i className="bi bi-chevron-right fs-3"></i>
            </button>
            <button
              title="btn-pagination"
              type="button"
              className="btn btn-sm btn-icon btn-active-light-primary mx-1"
              disabled={paginationConfig.isLast}
              onClick={() => handleNextPrevPage('last')}
            >
              <i className="bi bi-chevron-bar-right fs-3"></i>
            </button>
          </div>
        </nav>
      )}
    </>
  );
}

export default CustomDataTable;
